// Roll-cage estimate — how many cages this Large order needs, shown
// directly beneath the SCAN TOTE prompt as the order opens, and only
// there. That is the one moment the number changes what the picker
// does: they are standing still, about to go and fetch cages. Once
// they have scanned a tote and started walking it is history, so it
// goes away rather than following them down the pick.
//
// It sits INSIDE the hero, below the command words. The hero's vertical
// budget is tight on a handheld — a 120px glyph, clamp(48px,18vw,120px)
// command words and a min(160px,36vw) tick well — so the caller pairs
// this with .picking-hero--with-cages, which tightens the hero's 96px
// top padding to keep the card clear of the fold. If this ever stops
// appearing on a device, check that modifier before suspecting the
// data path.
//
// The estimate is a FLOOR: products with no recorded size contribute no
// volume (see rollCages.ts). Both renders therefore state the shortfall
// in units whenever there is one, and when NOTHING in the order is
// measured they say so outright instead of printing a confident "1".
import { Container, Ruler } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { RollCageEstimate as Estimate } from "./rollCages";
import "./RollCageEstimate.css";

// Always one decimal place — "1.0 roll cages" over "1 roll cage". A
// decimal takes the plural in English whatever its value, and a picker
// comparing 0.3 against 1.2 wants the two rendered the same width.
const formatCages = (cages: number): string => cages.toFixed(1);

const cagesLabel = (
    t: (key: string, vars?: Record<string, string>) => string,
    cages: number,
): string => t("picking_roll_cages_count", { count: formatCages(cages) });

const missingLabel = (
    t: (key: string, vars?: Record<string, string>) => string,
    units: number,
): string => (units === 1
    ? t("picking_roll_cages_missing_one")
    : t("picking_roll_cages_missing", { count: String(units) }));

export const RollCageCard = ({ estimate }: { estimate: Estimate }) => {
    const { t } = useTranslation();
    // Nothing measured at all — a cage count here would be invented.
    // Say what we don't know; the picker judges it themselves.
    const unknown = estimate.cages === 0;
    // The order's CBM is deliberately NOT shown. Pickers don't work in
    // cubic metres, and it cost a row in the tightest part of the hero.
    // It stays on the estimate for anything that wants to reason about
    // the number — just not on the picker's screen.

    return (
        <div className="rc-card" role="status">
            <div className="rc-card-main">
                <Container className="rc-card-icon" size={26} strokeWidth={1.7} aria-hidden="true" />
                <span className="rc-card-count">
                    {unknown ? t("picking_roll_cages_unknown") : cagesLabel(t, estimate.cages)}
                </span>
            </div>
            {estimate.missingDimUnits > 0 && (
                <div className="rc-card-missing">
                    <Ruler size={15} strokeWidth={2} aria-hidden="true" />
                    <span>{missingLabel(t, estimate.missingDimUnits)}</span>
                </div>
            )}
        </div>
    );
};
